import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { lpcoEnvs, lpcoUtils } from "@/tz/go/egaz/osci/lpco/comn"; // 시스템 공통 유틸
import { Page, Group, Layout, Button, Grid, ExcelUpload, FormControl, Table } from "@/comn/components";
import {
    useForm,
    useToast,
    useFetch,
    useModal,
    useAuth,
    useStore,
    useGrid,
    usePopup,
    useResource,
    useExcel,
    usePage,
} from "@/comn/hooks";
import {
    BASE,
    URLS,
    APIS,
    SF_LPCO_INFO_R,
    SG_LPCO_PRCS_LIST_R,
    withAprvColumn,
} from "./services/OsciCfgnPrcsService";
import { CommonErrors } from "@/comn/components";

/*
 * @ 화면 컴포넌트 주석
 * @ 메타 시스템을 참고 하여 표준 단어와 용어를 사용하여 작성
 * @ !== ... ==! 치환 부호는 개발 완료 후 한글 주석을 인지하여 제거하기 위한 표시
 * @ 기본 여러줄의 주석 형태를 사용
 * @ 포맷
 * @ [화면명 영문]
 * @ [지환부호시작] [화면명 한글] [치환부호종료]
 *
 * 화면명 영문
 * !== 화면명 한글 ==!
 */

/*
 * LPCO Process Details
 * !== LPCO Process 상세 ==!
 */
export const OSCI0203011S = () => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0004"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    const { prcsMnId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const { openPopup } = usePopup();
    const [lpcoPrcsList, setLpcoPrcsList] = useState(comnUtils.getGridData([]));
    const [appTpCd, setAppTpCd] = useState("");

    // ORG LIST
    const [orgCdList, setOrgCdList] = useState([]);
    // ORG LPCO LIST
    const [orgLpcoList, setOrgLpcoList] = useState([]);

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0116" },
            { area: "comnCd", comnCd: "OSC0120" },
            { area: "comnCd", comnCd: "OSC0113" },
            { area: "comnCd", comnCd: "OSC0143" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // LPCO Process Details !== LPCO Process 상세 ==!
        lpcoInfoDtl: useForm({
            defaultSchema: SF_LPCO_INFO_R,
            defaultValues: {
                orgCd: auth.get("orgCd"),
                orgNm: auth.get("orgNm"),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Process List !== Process 목록 ==!
        lpcoPrcsList: useGrid({
            defaultSchema: SG_LPCO_PRCS_LIST_R,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    const render = {
        grid_LpcoRList: {
            head: {},
            cell: {},
            edit: {},
        },
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        /* Process configuration parameters !== 프로세스 설정 파라미터 조회 ==! */
        getPrcsPrmt: useFetch({
            api: () => APIS.getPrcsPrmt(),
            onSuccess: (data) => {
                const prmt = data?.prmt?.content || {};
                // Show the approval column only when configurable stages exist !== 결재 설정 가능 단계가 있을 때만 컬럼 표시 ==!
                if ((prmt.aprvAblList || []).length > 0) {
                    grid.lpcoPrcsList.setSchema(withAprvColumn(SG_LPCO_PRCS_LIST_R, false));
                }
            },
            onError: () => {},
            showToast: false,
        }),
        getAppDtl: useFetch({
            api: (data) => {
                return APIS.getAppDtl(data);
            },
            onSuccess: (data) => {
                console.log("getAppDtl onSuccess ==================== ", data);
                form.lpcoInfoDtl.setValues(data.appInfo.content.info);
                setLpcoPrcsList(comnUtils.getGridData(data.appInfo.content.prcsList));
            },
            onError: () => {},
            showToast: false,
        }),
        deleteAppPrcs: useFetch({
            api: (prcsMnId) => APIS.deleteAppPrcs(prcsMnId),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00071",
                    onConfirm: () => {
                        navigate(URLS.osci0203010q);
                    },
                    onCancel: () => {
                        navigate(URLS.osci0203010q);
                    },
                });
            },
            onError: (err) => {
                modal.openModal({ content: err.response.data.errorMessage });
            },
            showToast: true,
        }),
    };

    /*
     * 화면에서 사용하는 이벤트, 함수를 선언
     * 기본 변수명은 메타 단어 조합 카멜 표기법을 따름
     * fetch 를 호출 하는 메소드: fetch 명과 동일하게 작성
     * 버튼이나 그리드의 이벤트의 경우 : [이벤트종류]_[이벤트대상타입]_[이벤트대상명]
     * 이벤트 대상 타입 : Btn , Grid ... 카멜표기법으로 표시, 첫글자 대문자
     * 이벤트 명 : 카멜표기법으로 표시, 첫글자 대문자
     *
     * const handler = {
     *      [handler 이름] : ()=>{ 함수 },
     *      click_Btn_Save : 저장버튼 클릭
     *      click_Grid_RpckItmAppList : 재포장 품목 신청서 그리드 클릭
     *      ...
     * }
     */
    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        deleteAppPrcs: () => {
            modal.openModal({
                content: "msg.osc.00095",
                onConfirm: () => {
                    fetch.deleteAppPrcs.fetch(prcsMnId);
                },
            });
        },
        refreshOrgCdAllList: async () => {
            setOrgCdList((await lpcoUtils.getOrgCdAllList()) || []);
        },
        refreshOrgLpcoList: async (usrOrgCd: any) => {
            setOrgLpcoList((await lpcoUtils.getOrgLpcoByPrcsList(usrOrgCd)) || []);
        },
        setAppDtl: (appFmId: any) => {
            fetch.getAppDtl.fetch(appFmId);
        },
    };

    useEffect(() => {
        fetch.getPrcsPrmt.fetch(); // process configuration parameters !== 프로세스 설정 파라미터 ==!
        handler.setAppDtl(prcsMnId);
        handler.refreshOrgCdAllList();
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Title title={"T_LPCO_APP_INFO"} titleSize={2}></Group.Title>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.lpcoInfoDtl.schema.orgNm}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.lpcoInfoDtl.schema.appNm}
                                onChange={(ev: any) => {
                                    console.log(ev);
                                }}
                            ></Group.Control>
                            <Group.Control {...form.lpcoInfoDtl.schema.appTpCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.lpcoInfoDtl.schema.lpcoAplyTpCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.lpcoInfoDtl.schema.appdDt}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Title title={"T_PRCS"} titleSize={2}></Group.Title>
                    {/*
                     * 그리드
                     * @ 그리드 스키마 주입 : {...grid.[그리드이름].grid}
                     * @ 데이터 data={fetch.[fetch 명].data?.[api 리턴 vo 명]}
                     * @ 셀클릭이벤트 연결 : onCellClick={handler.[그리드 이벤트 핸들러명]}
                     */}
                    <Grid
                        {...grid.lpcoPrcsList.grid}
                        data={lpcoPrcsList} /* fetch.getRpckItmAppList.data?.lpcoPrcsList */
                        render={render.grid_LpcoRList}
                        // onCellClick={handler.click_Grid_RpckItmAppList}
                        // onRowClick={handler.click_GridRow_RpckItmAppList}
                    />
                </Group.Body>
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.osci0203010q);
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    {/* <Button role="edit" onClick={handler.click_Btn_AddGridLpcoAtcdFileList}></Button> */}
                    <Button
                        role="edit"
                        onClick={() => {
                            navigate(`${URLS.osci0203012s}/${prcsMnId}`);
                        }}
                    ></Button>
                    <Button role="delete" onClick={handler.deleteAppPrcs}></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
